import { useMutation, useQuery } from '@tanstack/react-query'
import { SlidesFilterParams, CreateSlideParams, UpdateSlideParams } from '@/types/slide'
import { useSlidesStore } from '@/store'
import { SortOrderEnum } from '@/enums'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useSlides = (params: SlidesFilterParams) => {
  const fetchSlides = useSlidesStore(state => state.fetchSlides)

  return useQuery({
    queryKey: ['slides', params],
    queryFn: () => fetchSlides(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useSlide = (id: string) => {
  const getSlide = useSlidesStore(state => state.getSlide)

  return useQuery({
    queryKey: ['slide', id],
    queryFn: () => getSlide(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateSlide = () => {
  const createSlide = useSlidesStore(state => state.createSlide)

  return useMutation({
    mutationFn: (params: CreateSlideParams) => createSlide(params),
    onSuccess: () => {}
  })
}

export const useUpdateSlide = () => {
  const updateSlide = useSlidesStore(state => state.updateSlide)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateSlideParams }) => updateSlide(id, params),
    onSuccess: () => {}
  })
}

export const useSortSlide = () => {
  const sortSlide = useSlidesStore(state => state.sortSlide)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortSlide(id, type),
    onSuccess: () => {}
  })
}

export const useDeleteSlide = () => {
  const deleteSlide = useSlidesStore(state => state.deleteSlide)

  return useMutation({
    mutationFn: (id: string) => deleteSlide(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteSlides = () => {
  const bulkDeleteSlides = useSlidesStore(state => state.bulkDeleteSlides)
  const deselectAllSlides = useSlidesStore(state => state.deselectAllSlides)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteSlides(ids),
    onSuccess: response => {
      deselectAllSlides()
      handleBulkDeleteSuccess(response)
    }
  })
}
